// ============================================================================
// THE DATA CONTRACT — remote.ts
//
// The real implementation: fetch() against the Worker. Nothing but calls and
// shape-mapping — the browser never queries Postgres directly, and every read is
// owner-scoped on the server side from the JWT.
//
// WHAT IS REAL, as of 2026-08-26: the JOURNAL and its voice memos (V0/V1), and
// SCHOOL, now whole — courses, the term, assignments and announcements out of
// the Canvas connector; lectures and their timestamped transcripts out of V1's
// lectures API; and the two AI surfaces, the syllabus extract and the lecture
// study set, each read and each generable. Every other domain's endpoint
// arrives with the version that owns it, and until then its method throws
// NotImplementedError — a named error carrying a sentence that says what the
// owner is waiting for, rather than a fetch that 404s and leaves a card blank.
// The URLs below are already correct; landing a route means deleting a throw.
//
// ONE OF THOSE SENTENCES NAMES NO VERSION, deliberately: `getClassOccurrences`.
// Berkeley's class meeting times are not in Canvas at all and no amount of
// syncing will produce them, so it names bCal instead. A version number there
// would send the owner off to wait for the wrong thing.
//
// Auth: Supabase issues the JWT in the browser (lib/auth/supabase.ts — auth only,
// never queries) and the Worker verifies it against the project's public JWKS.
// ============================================================================

import type {
  Announcement,
  Assignment,
  CaptureToken,
  ClassOccurrence,
  Connector,
  Course,
  DataStatus,
  DesktopWorkerStatus,
  Flashcard,
  ISODate,
  InterestSave,
  InterestTopic,
  JournalEntry,
  LearningItem,
  LearningStreak,
  LearningTrack,
  Lecture,
  LectureSummary,
  PracticeQuestion,
  SyllabusDocument,
  SyllabusExtract,
  SyncResult,
  SyncStatusRow,
  Task,
  Term,
  TranscriptSpeaker,
} from '@app/shared'

// Called on the read path, not just in tests: the type system stops at the network boundary, and
// an AI artifact that cannot say which model made it must not reach the screen.
import { hasProvenance, onlyProvenanced } from '@app/shared'

import { getAccessToken, remoteConfig } from '../auth/supabase'
import type {
  AnnouncementFilter,
  AssignmentFilter,
  Clock,
  DataAPI,
  DateRange,
  JournalDayContext,
  InterestSaveDetail,
  InterestsOverview,
  JournalEntryInput,
  JournalVoiceMemo,
  JournalStats,
  LearningSessionInput,
  LearningTrackDetail,
  LearningTrackLibrary,
  LearningTrackSummary,
  LectureTranscript,
  SearchResults,
  SaveFilter,
  StudySet,
  TaskFilter,
  TaskInput,
  TaskPatch,
  VoiceMemoUpload,
} from './types'

// Chat (11a/11b) and capture (11c). Its own import line rather than another name in the block
// above: three screen ports are in flight at once and a one-identifier edit inside a shared import
// list is a merge conflict for no benefit.
import type {
  CaptureInput,
  CaptureResult,
  ChatAttachment,
  ChatMessage,
  ChatProposal,
  ChatProposalDecision,
  ChatProposalResult,
  ChatScope,
  ChatScopeId,
  ChatSendInput,
  ChatSendResult,
  ChatSource,
  ChatStarter,
  ChatThread,
  ChatThreadDetail,
  ClipPreview,
} from './types'

// Calendar (11a/11b). Its own import lines, for the merge reason the block above states.
import type { CalendarBlock, CalendarRange } from '@app/shared'
import type { CalendarBlockInput, CalendarBlockPatch } from './types'

// Notifications · Web Push (NOTIF-1b). Its own line, same convention.
import type {
  NotificationFilter,
  NotificationRecord,
  NotificationSettings,
  NotificationSettingsPatch,
  PushDevice,
  PushSubscribeInput,
  PushTestResult,
} from './types'

// Mail · Google accounts for read-only Gmail (Day page, Lane 1a). Same convention.
import type { MailAccount, MailDisconnectResult, MailMessageFilter, MailMessagePage } from './types'
// Mail · triage, grading and rules (Day page prototype). Same convention.
import type {
  MailCorrectionReason,
  MailItemState,
  MailRule,
  MailRuleInput,
  MailTriage,
  MailTriageItem,
  MailTriageRunResult,
  MailVerdict,
} from './types'

// The phone lecture upload (`/record`). Same reason for the separate line.
import type { MailAudit, MailTriageQuery } from './types'
// Mail · next steps (Day page, Lane 6). Same convention.
import type { MailProposalDecision } from './types'
import type { CapturedLecture, LectureUpload, RegisteredUpload } from './types'
// The durable upload queue (REC-1b). Same convention; `UploadApiError` is a value, not a type.
import { UploadApiError } from './types'
import type {
  UploadBlobInput,
  UploadBlobResult,
  UploadRegistration,
  UploadRegistrationInput,
  UploadServerStatus,
  UploadState,
} from './types'
// The phone's own uploader's credential (REC-1, 2026-09-24). Same convention — its own line.
import type { CaptureTokenInput, CreatedCaptureToken } from './types'

export class NotConfiguredError extends Error {
  constructor(path: string) {
    super(
      `Remote data is not available (${path}). This build has no VITE_SUPABASE_URL, ` +
        `VITE_SUPABASE_ANON_KEY and VITE_API_BASE_URL, so it runs on demo data. Set all three in ` +
        `apps/web/.env.local to switch over.`,
    )
    this.name = 'NotConfiguredError'
  }
}

export class NotSignedInError extends Error {
  constructor(path: string) {
    super(`Not signed in, so ${path} was not called. Sign in and the request will go through.`)
    this.name = 'NotSignedInError'
  }
}

/**
 * A method whose Worker endpoint has not been built yet.
 *
 * It exists so that a card in the UI can say "Saves arrive with V5" instead of rendering an
 * empty list that is indistinguishable from "you have nothing due". The data contract requires
 * every method to EXIST here (demo.test.ts asserts it); it does not require every method to work
 * before the version that owns it.
 */
export class NotImplementedError extends Error {
  constructor(what: string, version: string) {
    super(`${what} arrives with ${version}. The Worker has no endpoint for it yet.`)
    this.name = 'NotImplementedError'
  }
}

// `notYet`, the helper that wrapped a method in that refusal, was deleted 2026-08-26 — the day
// the last stub landed. The class stays: main.tsx's shouldRetry and the tests still name it,
// and any future not-yet-built method should bring the helper back rather than fake an answer.

// ── School wire shapes (V2) ─────────────────────────────────────────────────
//
// The Worker answers a SUPERSET of the browser's `Course` / `Assignment` / `Announcement`
// (apps/worker/src/lib/school.ts): enrollment role, Canvas permalinks, grading type, the raw
// syllabus HTML. These interfaces describe what comes off the wire; the mappers below narrow it
// to the vocabulary the screens speak. Nothing is lost — the extra fields are one documented
// GET away, which is the machine-readability rule holding in both directions.

interface WireCourse {
  id: string
  code: string
  name: string
  instructor: string | null
  units: number | null
  term: string
  meetings: never[]
  gradeSoFar: number | null
  gradeLetter: string | null
  gradeHidden: boolean
  unreadAnnouncements: number
}

interface WireAssignment {
  id: string
  courseId: string | null
  courseCode: string | null
  title: string
  kind: Assignment['kind']
  dueAt: string | null
  assignedAt: string | null
  status: Assignment['status']
  score: number | null
  pointsPossible: number | null
  // ── THE DETAIL PEEK'S FIELDS ──────────────────────────────────────────────
  // Four of these five have been on the wire since the connector shipped and were simply not
  // read; `description` arrived with supabase/migrations/20260829094500_assignment_description.sql.
  // Declared optional because a Worker deployed BEFORE that migration answers without them, and
  // a browser that renders "undefined" for a field its server does not have yet is worse than a
  // browser that says the field is absent.
  lockAt?: string | null
  gradingType?: string | null
  submissionTypes?: string[] | null
  htmlUrl?: string | null
  description?: string | null
}

interface WireAnnouncement {
  id: string
  courseId: string | null
  courseCode: string | null
  author: string | null
  title: string
  body: string
  postedAt: string | null
  read: boolean
}

const toCourse = (row: WireCourse): Course => ({
  id: row.id,
  code: row.code,
  name: row.name,
  instructor: row.instructor,
  // Canvas has no credit-hours field, so this is null for every synced course and the card
  // omits the segment. It is never derived from the course code.
  units: row.units,
  term: row.term,
  // Always empty from Canvas — UNKNOWN, not "never meets". The card says exactly that.
  meetings: row.meetings,
  gradeSoFar: row.gradeSoFar,
  gradeLetter: row.gradeLetter,
  gradeHidden: row.gradeHidden,
  unreadAnnouncements: row.unreadAnnouncements,
})

const toAssignment = (row: WireAssignment): Assignment => ({
  id: row.id,
  courseId: row.courseId ?? '',
  courseCode: row.courseCode ?? '',
  title: row.title,
  kind: row.kind,
  dueAt: row.dueAt,
  assignedAt: row.assignedAt,
  status: row.status,
  score: row.score,
  // A points-possible of null means Canvas gave none; 0 is the only honest number to divide a
  // score by in that case, and the table only ever prints it beside a score that exists.
  pointsPossible: row.pointsPossible ?? 0,
  // ABSENT AND NULL COLLAPSE TO THE SAME THING HERE, and both mean "Canvas said nothing" — the
  // peek prints a sentence for each field it does not have rather than a blank or a default.
  lockAt: row.lockAt ?? null,
  gradingType: row.gradingType ?? null,
  submissionTypes: row.submissionTypes ?? [],
  htmlUrl: row.htmlUrl ?? null,
  description: row.description ?? '',
})

// ── captured-lecture wire shape (the phone upload lane) ─────────────────────
//
// `LectureSchema` in apps/worker/src/lib/audio.ts, exactly. `audioJobId` is the one field
// dropped: no screen has anything to say about a queue row's id, and a contract field nothing
// renders is a contract field that quietly stops being maintained. It is still one documented
// GET away for anything reading the API directly.

interface WireCapturedLecture {
  id: string
  courseCode: string | null
  courseId: string | null
  title: string | null
  index: number | null
  date: string
  durationSec: number | null
  status: CapturedLecture['status']
  audioLocation: CapturedLecture['audioLocation']
  audioJobId: string | null
  hasTranscript: boolean
}

/**
 * The same wire row, narrowed to the SCHOOL screens' vocabulary — where a lecture always sits
 * inside a course.
 *
 * The one row this drops is the UNFILED one: `courseCode: null`, which the drop folder and a
 * hurried phone upload both produce. It is not lost and it is not hidden — `/record` lists every
 * one of them and is where a course gets picked (`assignLectureCourse`). It is dropped HERE
 * because a course page cannot draw a lecture that is not in a course, and filing it under the
 * course whose page happens to be open would be the app guessing. The same rule the Worker's
 * search applies to a lecture hit it cannot address (apps/worker/src/lib/search.ts).
 *
 * `index` and `title` ride through as null when they are null. Nothing in the pipeline numbers a
 * recording, so most real lectures have no index at all, and the row renders under its date and
 * its own filename instead of under an invented "Lecture 3".
 */
const toSchoolLecture = (row: WireCapturedLecture): Lecture | null => {
  if (!row.courseCode) return null
  return {
    id: row.id,
    // Resolved by the Worker from the code, and null when no synced course carries it — a
    // lecture from a dropped course. Empty string keeps `Lecture.courseId` a string, and no
    // course-scoped read ever matches it, which is exactly right for a course that is gone.
    courseId: row.courseId ?? '',
    courseCode: row.courseCode,
    index: row.index,
    title: row.title,
    date: row.date as ISODate,
    durationSec: row.durationSec,
    status: row.status,
    audioLocation: row.audioLocation,
  }
}

interface WireTranscript {
  id: string
  source: 'lecture' | 'voice_memo'
  lectureId: string | null
  wordCount: number
  language: string
  model: string | null
  /** The voices in the recording. Null when nobody worked out who was speaking — see @app/shared. */
  speakers: TranscriptSpeaker[] | null
  createdAt: string
}

interface WireTranscriptSegment {
  id: string
  index: number
  startMs: number
  endMs: number
  speaker: string | null
  text: string
  /** Whisper's confidence in this passage. Null when it reported none — see @app/shared. */
  avgLogprob: number | null
}

const toCapturedLecture = (row: WireCapturedLecture): CapturedLecture => ({
  id: row.id,
  courseCode: row.courseCode,
  title: row.title,
  index: row.index,
  date: row.date as ISODate,
  durationSec: row.durationSec,
  status: row.status,
  audioLocation: row.audioLocation,
  hasTranscript: row.hasTranscript,
})

/**
 * The 100 MB refusal, in the owner's words rather than Cloudflare's.
 *
 * It names both ways out, because "too large" with no way forward is where a lecture gets lost:
 * the recording can be split, or dropped into the desktop worker's folder, which never touches
 * this endpoint at all (apps/worker/src/routes/audio.ts documents why the ceiling exists).
 */
const tooLarge = (bytes: number, maxBytes: number): Error =>
  new Error(
    `That file is ${Math.round(bytes / 1024 / 1024)} MB and one upload cannot exceed ` +
      `${Math.round(maxBytes / 1024 / 1024)} MB — that is Cloudflare's request-body limit, not a ` +
      'setting here. Split the recording, or drop the file into the desktop worker’s folder, ' +
      'which does not go through this endpoint at all.',
  )

/** Read at call time, not module load: a test can stub the env, and import order stops mattering. */
function apiBase(): string {
  return remoteConfig()?.apiBaseUrl ?? ''
}

async function call<T>(path: string, init?: RequestInit): Promise<T> {
  const base = apiBase()
  if (!base) throw new NotConfiguredError(path)

  const token = await getAccessToken()
  if (!token) throw new NotSignedInError(path)

  const res = await fetch(`${base}${path}`, {
    ...init,
    headers: {
      'content-type': 'application/json',
      authorization: `Bearer ${token}`,
      ...init?.headers,
    },
  })

  if (!res.ok) {
    // The Worker answers every failure as { error, message } with a sentence in it. Surfacing that
    // sentence is the difference between "PUT /api/journal/2026-09-16 → 503" and "this deployment
    // has no Supabase project configured".
    let detail = ''
    try {
      const body = (await res.json()) as { message?: string }
      detail = typeof body.message === 'string' ? body.message : ''
    } catch {
      /* a non-JSON error body is not worth a second failure */
    }
    throw new Error(detail || `${path} → ${res.status}`)
  }
  return (await res.json()) as T
}

/**
 * The same request as `call`, for an endpoint whose body is TEXT rather than JSON.
 *
 * One caller today (`/api/learning/export`, which answers markdown). It repeats `call`'s failure
 * handling rather than sharing it because the difference is exactly one line — and a failure body
 * is still JSON even when the success body is not, so the owner keeps getting the Worker's own
 * sentence instead of a status code.
 */
async function callText(path: string, init?: RequestInit): Promise<string> {
  const base = apiBase()
  if (!base) throw new NotConfiguredError(path)

  const token = await getAccessToken()
  if (!token) throw new NotSignedInError(path)

  const res = await fetch(`${base}${path}`, {
    ...init,
    headers: { authorization: `Bearer ${token}`, ...init?.headers },
  })

  if (!res.ok) {
    let detail = ''
    try {
      const body = (await res.json()) as { message?: string }
      detail = typeof body.message === 'string' ? body.message : ''
    } catch {
      /* a non-JSON error body is not worth a second failure */
    }
    throw new Error(detail || `${path} → ${res.status}`)
  }
  return await res.text()
}

// ── the upload queue's own transport (REC-1b) ───────────────────────────────
//
// `call()` above is the right shape for a screen: it throws the Worker's sentence and nothing
// else. These four helpers keep the STATUS and the ERROR CODE, because the queue's next move
// depends on which refusal it got, and the owner's recording depends on the queue's next move.

const UPLOAD_OFFLINE =
  'The recording did not go up — the network or the API stopped answering. It is saved on this ' +
  'phone and will be sent again.'

const SIGNED_OUT = 'Signed out — open LifeOS to continue.'

/** One authenticated request, with the response handed back whole rather than thrown away. */
async function uploadFetch(path: string, init?: RequestInit): Promise<Response> {
  const base = apiBase()
  if (!base) throw new UploadApiError(0, 'not_configured', new NotConfiguredError(path).message)

  const token = await getAccessToken()
  // A 401 is what the queue pauses on, so a MISSING token has to arrive as the same thing a
  // rejected one does. Two ways of saying "signed out" would be two code paths to keep correct.
  if (!token) throw new UploadApiError(401, 'not_signed_in', SIGNED_OUT)

  try {
    return await fetch(`${base}${path}`, {
      ...init,
      headers: { authorization: `Bearer ${token}`, ...init?.headers },
    })
  } catch {
    // fetch rejects only for a transport failure. Offline is a WAIT, never a reason to conclude
    // anything about what the server did or did not do with a request it may well have received.
    throw new UploadApiError(0, 'offline', UPLOAD_OFFLINE)
  }
}

/** The body, or null. A Worker that answered with no JSON is not a second failure to report. */
async function readJson(res: Response): Promise<unknown> {
  try {
    return await res.json()
  } catch {
    return null
  }
}

function uploadError(res: Response, body: unknown): UploadApiError {
  const shape = (body ?? {}) as { error?: string; message?: string; job?: { status?: string } }
  const retryAfter = Number(res.headers.get('retry-after'))
  return new UploadApiError(
    res.status,
    shape.error ?? `http_${res.status}`,
    shape.message || `That request failed (${res.status}).`,
    {
      serverStatus: asServerStatus(shape.job?.status),
      retryAfterSec: Number.isFinite(retryAfter) && retryAfter > 0 ? retryAfter : null,
    },
  )
}

const SERVER_STATUSES: readonly string[] = [
  'awaiting_audio',
  'uploading',
  'queued',
  'claimed',
  'transcribing',
  'done',
  'failed',
  'cancelled',
]

/**
 * A status the Worker sent, or null — never a default.
 *
 * A Worker deployed ahead of this build could name a state this one has never heard of. Null
 * means "it said something I cannot act on", which the queue handles by asking again; guessing
 * the nearest known status is how a client concludes a recording arrived when it did not.
 */
function asServerStatus(value: unknown): UploadServerStatus | null {
  return typeof value === 'string' && SERVER_STATUSES.includes(value)
    ? (value as UploadServerStatus)
    : null
}

// ── tasks · the wire shape ──────────────────────────────────────────────────
//
// What GET /api/tasks actually answers. It is `Task` plus the two infrastructure fields the
// Worker documents and the screens do not use.

interface WireTask {
  id: string
  title: string
  done: boolean
  due: string | null
  list: string
  priority: string
  rrule: string | null
  reminderAt: string | null
  createdAt: string
  completedAt: string | null
  source: string
  externalId: string | null
  estimateMin: number | null
  proposalId?: string | null
}

function toTask(wire: WireTask): Task {
  return {
    id: wire.id,
    title: wire.title,
    done: wire.done,
    due: (wire.due ?? null) as ISODate | null,
    list: wire.list,
    priority: wire.priority as Task['priority'],
    rrule: wire.rrule,
    reminderAt: wire.reminderAt,
    createdAt: wire.createdAt,
    completedAt: wire.completedAt,
    // `?? null` rather than a bare read: a Worker deployed before the calendar migration was
    // applied omits the field entirely, and `undefined` would make every task look like it had
    // an estimate of "not a number" instead of none.
    estimateMin: wire.estimateMin ?? null,
    // Same `?? null` for the same reason: a Worker deployed before the chat-proposals migration
    // omits it, and `undefined` would make every reminder look like it came from somewhere.
    proposalId: wire.proposalId ?? null,
  }
}

/**
 * 09:00 on a given day, as an instant, in the browser's own timezone.
 *
 * The ⌘K palette's `reminder` type is the one place the app turns a DAY into a TIME, and the demo
 * implementation picks 09:00 too — the two have to agree or the same capture would produce a
 * different nudge depending on which mode you were in.
 */
function morningOf(date: string): string {
  const [year, month, day] = date.split('-').map(Number)
  return new Date(year!, month! - 1, day!, 9, 0, 0, 0).toISOString()
}

// ── Chat wire shapes (V4) ───────────────────────────────────────────────────
//
// A SUPERSET of what the components render, and deliberately so. The Worker answers a thread's
// `persona` and an assistant message's `model` and `tier` because provenance on anything a model
// made is a product rule, not a nicety (docs/design/FIDELITY.md) — and the machine-readability
// rule says an agent reading /api/chat/threads must be able to see which model said what. The
// mappers below narrow to the vocabulary the screens speak; nothing is lost, it is one documented
// GET away.

interface WireChatThread {
  id: string
  title: string
  scopeId: string
  scopeLabel: string
  persona: 'mirror' | 'tutor'
  messageCount: number
  startedAt: string
  updatedAt: string
}

interface WireChatMessage {
  id: string
  role: 'user' | 'assistant'
  text: string
  sources: ChatSource[]
  scopeLimit: { text: string; widenTo: string; widenLabel: string } | null
  model: string | null
  tier: 'quick' | 'deep' | null
  // Declared OPTIONAL because a Worker deployed before Chat v3 answers without them, and a
  // browser that renders `undefined.map` is worse than one that renders an empty conversation.
  attachments?: ChatAttachment[]
  proposals?: ChatProposal[]
  createdAt: string
}

interface WireChatScope {
  id: string
  label: string
  size: { count: number; noun: string }[]
  domain: ChatScope['domain']
}

interface WireChatStarter {
  id: string
  prompt: string
  scopeId: string
  reads: { count: number; noun: string }[]
  domain: ChatStarter['domain']
}

const toChatThread = (row: WireChatThread): ChatThread => ({
  id: row.id,
  title: row.title,
  scopeId: row.scopeId as ChatScopeId,
  scopeLabel: row.scopeLabel,
  messageCount: row.messageCount,
  startedAt: row.startedAt,
  updatedAt: row.updatedAt,
})

const toChatMessage = (row: WireChatMessage): ChatMessage => ({
  id: row.id,
  role: row.role,
  text: row.text,
  // Never defaulted to something non-empty. An assistant message with no sources is the state the
  // conversation renders behind a red strip, and inventing one here would hide it.
  sources: row.sources ?? [],
  scopeLimit: row.scopeLimit
    ? { ...row.scopeLimit, widenTo: row.scopeLimit.widenTo as ChatScopeId }
    : null,
  attachments: row.attachments ?? [],
  // A PROPOSAL WITH NO EVIDENCE IS NOT RENDERED, and it is filtered HERE rather than only in the
  // card: the Worker already drops those before storing, so anything that reaches this line
  // uncited came off an older deployment or a hand-written row. The card would have nothing to
  // put on its evidence line, and a card that cannot say where it came from is the one thing
  // this whole feature exists to not show.
  proposals: (row.proposals ?? []).filter((p) => p.evidence.length > 0),
})

/**
 * A lecture's whole study set, in one request (V4).
 *
 * `onlyProvenanced` is applied HERE, at the network boundary, and not only in a test: the type
 * system stops at `fetch`, and a row written by an older schema or a half-finished generation can
 * arrive shaped like an artifact and carrying nothing. Anything a model made and cannot be labelled
 * with the model that made it does not reach the screen (packages/shared/src/school.ts).
 */
async function readStudySet(lectureId: string): Promise<{
  summary: LectureSummary | null
  flashcards: Flashcard[]
  practiceQuestions: PracticeQuestion[]
}> {
  const wire = await call<{
    summary: LectureSummary | null
    flashcards: Flashcard[]
    practiceQuestions: PracticeQuestion[]
  }>(`/api/lectures/${encodeURIComponent(lectureId)}/study`)
  return {
    summary: hasProvenance(wire.summary) ? wire.summary : null,
    flashcards: onlyProvenanced(wire.flashcards),
    practiceQuestions: onlyProvenanced(wire.practiceQuestions),
  }
}

// ── The archive's wire shape (V6) ───────────────────────────────────────────
//
// Its own block, for the merge reason the import lines above state. The Worker answers with
// `preview` + `previewTruncated` where the browser's `JournalEntry` carries `text`, and the
// difference is the whole point of the endpoint: the archive is a list of days, not a copy of
// everything ever written. `previewTruncated` is not mapped through because the preview already
// ends in an ellipsis when it is true — a second signal for the same fact is a second thing that
// can disagree with the first.
interface WireJournalSummary {
  date: string
  prompt: string
  preview: string
  previewTruncated: boolean
  wordCount: number
  mood: number | null
  energy: number | null
  tags: string[]
  voiceMemoCount: number
  updatedAt: string
}

export const remote: DataAPI = {
  isDemo: false,

  // The wall clock is the browser's in remote mode; there is no round trip for it.
  async getClock(): Promise<Clock> {
    const now = new Date()
    const date = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`
    return { date, minutesIntoDay: now.getHours() * 60 + now.getMinutes() }
  },

  // ── sync status — live since V2 ───────────────────────────────────────────
  // The School header's Canvas chip reads this. Empty is a REAL answer here: it means no
  // connector has ever run, and the chip renders nothing rather than claiming a state.
  getSyncStatus: async (): Promise<SyncStatusRow[]> =>
    (await call<{ sources: SyncStatusRow[] }>('/api/connections/sources')).sources,

  // ── journal — the one domain that is real in V0 ───────────────────────────
  getJournalEntry: (date: ISODate) => call<JournalEntry | null>(`/api/journal/${date}`),

  saveJournalEntry: (input: JournalEntryInput) =>
    call<JournalEntry>(`/api/journal/${input.date}`, {
      method: 'PUT',
      body: JSON.stringify(input),
    }),

  // ── voice memos — live since V1 ───────────────────────────────────────────
  //
  // A memo has no table of its own: it IS its transcription job, joined server-side to whatever
  // transcript that job produced (apps/worker/src/routes/journal.ts). Empty is now a REAL answer
  // rather than a named absence, because a memo the owner never recorded is genuinely not there.
  //
  // `paragraphIndex` IS FILLED IN AS NULL, and that is a real answer rather than a patch over a
  // hole: nothing in the pipeline records where in the writing a memo was dropped — the recorder
  // never sends a position and no column holds one — so the honest value is "not known", which
  // the day page reads as "place it after the text" rather than at a guessed paragraph. Without
  // this the field arrives `undefined`, and the day page's `=== null` test silently drops every
  // memo off the page.
  getVoiceMemos: async (date: ISODate): Promise<JournalVoiceMemo[]> => {
    const memos = await call<Array<Omit<JournalVoiceMemo, 'paragraphIndex'>>>(
      `/api/journal/${date}/memos`,
    )
    return memos.map((memo) => ({ ...memo, paragraphIndex: null }))
  },

  uploadVoiceMemo: async ({ date, blob, durationSec }: VoiceMemoUpload) => {
    // A dropped connection arrives as `TypeError: Failed to fetch`, which is the browser talking
    // to a developer. The person reading this line is holding a recording that has not gone
    // anywhere yet and needs to know exactly that, plus that it is still recoverable.
    const OFFLINE =
      'The recording could not be sent — the network or the API did not answer. It is still in ' +
      'this tab: try again, and do not leave the page until it goes through.'
    const network = <T>(promise: Promise<T>): Promise<T> =>
      promise.catch((error: unknown) => {
        throw error instanceof TypeError ? new Error(OFFLINE) : error
      })

    // TWO CALLS, because that is the contract the phone and the browser share
    // (apps/worker/src/routes/audio.ts): register the job, then PUT the bytes at the URL it hands
    // back. The split is what makes a retry after a dropped upload reuse one job rather than
    // leaving a trail of half-registered memos.
    const contentType = blob.type || 'audio/webm'
    const registered = await network(
      call<{ jobId: string; uploadUrl: string; maxBytes: number }>('/api/audio/uploads', {
        method: 'POST',
        body: JSON.stringify({
          kind: 'memo',
          origin: 'web',
          journalDate: date,
          contentType,
          durationSec: Math.round(durationSec),
        }),
      }),
    )

    // The size ceiling is checked BEFORE the bytes go out, so a too-long recording costs a
    // sentence rather than a minute of uploading followed by a 413. The Worker enforces it too —
    // this is the copy that can explain itself while the audio is still in the tab.
    if (blob.size > registered.maxBytes) {
      throw new Error(
        `That recording is ${Math.round(blob.size / 1024 / 1024)} MB and one upload cannot exceed ` +
          `${Math.round(registered.maxBytes / 1024 / 1024)} MB. It is still in this tab until you ` +
          'leave the page — record shorter memos, or drop the file into the desktop worker’s folder.',
      )
    }

    const token = await getAccessToken()
    if (!token) throw new NotSignedInError(registered.uploadUrl)

    // NO Content-Length HEADER HERE, deliberately: it is a forbidden header name, so a browser
    // silently drops it — and the Worker 411s without one. Passing a Blob (not a stream) is what
    // makes the browser set it itself, which is the only way this request can succeed at all.
    const res = await network(
      fetch(registered.uploadUrl, {
        method: 'PUT',
        headers: { authorization: `Bearer ${token}`, 'content-type': contentType },
        body: blob,
      }),
    )
    if (!res.ok) {
      let detail = ''
      try {
        const body = (await res.json()) as { message?: string }
        detail = typeof body.message === 'string' ? body.message : ''
      } catch {
        /* a non-JSON error body is not worth a second failure */
      }
      throw new Error(detail || `Uploading the recording failed (${res.status}).`)
    }

    const { job } = (await res.json()) as {
      job: { id: string; journalDate: string | null; createdAt: string; durationSec: number | null }
    }
    return {
      id: job.id,
      date: (job.journalDate ?? date) as ISODate,
      recordedAt: job.createdAt,
      durationSec: job.durationSec ?? Math.round(durationSec),
      // `queued` and nothing else. The bytes are in R2; the desktop worker has not looked at them.
      status: 'queued' as const,
      transcriptId: null,
      transcriptText: null,
      paragraphIndex: null,
      error: null,
    }
  },

  // ── the archive — LIVE since V6 ───────────────────────────────────────────
  //
  // THE LIST DOES NOT CARRY THE ENTRIES. `GET /api/journal/entries` answers with a PREVIEW per
  // day — the first ~180 characters, ellipsis included when it was cut — because the archive
  // draws two clamped lines and a month of real writing is a chapter of a book. `text` below is
  // therefore the preview, and the day card renders exactly what it is given; the whole of a day
  // is one `GET /api/journal/{date}` away, which is the day the reader clicked into anyway.
  //
  // The filter chips ("Has voice", a tag, mood 3–5) run in the browser, over the month the
  // screen already asked for. That is not a shortcut: the archive draws a "no entry" row for
  // every blank day in the window, and a server-filtered list cannot say which days were blank —
  // it would take a second, unfiltered read to draw the gaps. Every row carries `mood`, `tags`
  // and `voiceMemoCount`, so one read answers both questions.
  getJournalEntries: async (range: DateRange): Promise<JournalEntry[]> => {
    const wire = await call<{ from: string; to: string; entries: WireJournalSummary[] }>(
      `/api/journal/entries?from=${encodeURIComponent(range.from)}&to=${encodeURIComponent(range.to)}`,
    )
    return wire.entries.map((row) => ({
      date: row.date as ISODate,
      prompt: row.prompt,
      text: row.preview,
      wordCount: row.wordCount,
      mood: row.mood,
      energy: row.energy,
      tags: row.tags,
      voiceMemoCount: row.voiceMemoCount,
      updatedAt: row.updatedAt,
    }))
  },

  // Totals, both streaks, the 26-week heatmap, month coverage and tag counts — all DERIVED on the
  // Worker from the entries themselves on every read. Nothing here is stored, so nothing here can
  // drift the first time an entry is written for a past day.
  getJournalStats: () => call<JournalStats>('/api/journal/stats'),

  // The "that day" strip, reading across the Google Calendar mirror, Tasks, Learning and
  // Interests. Every list can be empty, and empty means "nothing that day" — a true answer.
  getJournalDayContext: (date: ISODate) =>
    call<JournalDayContext>(`/api/journal/${date}/context`),

  // ── school — LIVE since V2 (the Canvas connector) ─────────────────────────
  //
  // These read what the connector already wrote, never Canvas itself. A bCourses outage
  // therefore makes the School screen STALE rather than empty, and the Connections screen — not
  // this one — is where the owner finds out how stale.
  //
  // The Worker answers a SUPERSET of these shapes (enrollment role, Canvas permalinks, the raw
  // syllabus HTML); the mapping below narrows it to what the browser's vocabulary carries. The
  // extra fields are not lost, they are one `/api/school/courses` away for anything that reads
  // the API directly, which is the point of the machine-readability rule.
  getTerm: () => call<Term | null>('/api/school/term'),

  getCourses: async (): Promise<Course[]> =>
    (await call<{ courses: WireCourse[] }>('/api/school/courses')).courses.map(toCourse),

  // LIVE since V2 (the Google Calendar mirror). Berkeley's class meeting times live in SIS and
  // reach a student through bCal, never through Canvas (docs/research/berkeley-data-sources.md
  // §B.7) — so this reads the mirrored Google Calendar instead. The Worker matches mirrored, timed
  // events against the owner's synced course codes — see apps/worker/src/lib/class-occurrences.ts
  // for the exact rule — so an event that is not provably a class (a dentist appointment, a study
  // group) never arrives here as one. Empty is a real answer: a day with no matching events, or a
  // day before Google Calendar has ever synced.
  getClassOccurrences: async (date: ISODate): Promise<ClassOccurrence[]> =>
    (
      await call<{ occurrences: ClassOccurrence[] }>(
        `/api/school/class-occurrences?date=${date}`,
      )
    ).occurrences,

  getAssignments: async (filter?: AssignmentFilter): Promise<Assignment[]> => {
    const params = new URLSearchParams()
    if (filter?.courseId) params.set('courseId', filter.courseId)
    if (filter?.status) params.set('status', filter.status)
    if (filter?.openOnly) params.set('needsAction', 'true')
    if (filter?.limit) params.set('limit', String(filter.limit))
    const { assignments } = await call<{ assignments: WireAssignment[] }>(
      `/api/school/assignments${params.size ? `?${params}` : ''}`,
    )
    // `dueBefore` is applied here rather than on the wire because an UNDATED assignment is not
    // "before" anything, and a PostgREST range filter would quietly sweep the nulls out of every
    // list that uses it. Undated work stays; it is only excluded from a question about dates.
    const rows = filter?.dueBefore
      ? assignments.filter((a) => a.dueAt !== null && a.dueAt.slice(0, 10) <= filter.dueBefore!)
      : assignments
    return rows.map(toAssignment)
  },
  // ── lectures and transcripts — LIVE since V1 ──────────────────────────────
  //
  // Both read routes shipped with the transcription system (apps/worker/src/routes/lectures.ts).
  // The mapping is where the honesty lives, and it is documented on `toSchoolLecture` above:
  // an unfiled recording is dropped rather than filed under whatever course page is open, and an
  // unnumbered one keeps its null index rather than being given a lecture number nobody counted.

  getLectures: async (courseId?: string): Promise<Lecture[]> => {
    const params = new URLSearchParams({ limit: '200' })
    // The Worker resolves the uuid to the course code the lectures table stores, and answers an
    // empty list for an id no synced course carries — never the unfiltered list.
    if (courseId) params.set('courseId', courseId)
    const { lectures } = await call<{ lectures: WireCapturedLecture[] }>(`/api/lectures?${params}`)
    return lectures
      .map(toSchoolLecture)
      .filter((lecture): lecture is Lecture => lecture !== null)
  },

  getLectureTranscript: async (lectureId: string): Promise<LectureTranscript | null> => {
    const wire = await call<{
      lecture: WireCapturedLecture
      transcript: WireTranscript | null
      segments: WireTranscriptSegment[]
    }>(`/api/lectures/${encodeURIComponent(lectureId)}`)

    const lecture = toSchoolLecture(wire.lecture)
    // NULL IS A REAL ANSWER, three ways, and the lecture screen says each of them in words:
    // the recording is not filed under a course, or the desktop worker has not transcribed it
    // yet, or it produced no speech. None of the three is an error, and none of them renders an
    // empty transcript panel that reads as "this lecture was about nothing".
    if (!lecture || !wire.transcript) return null

    return {
      lecture,
      transcript: {
        id: wire.transcript.id,
        source: wire.transcript.source,
        lectureId: wire.transcript.lectureId,
        wordCount: wire.transcript.wordCount,
        language: wire.transcript.language,
        // Null rides through. The panel prints the word count alone rather than naming a
        // transcriber the row never recorded.
        model: wire.transcript.model,
        // `?? null` guards the deploy window, exactly as `avgLogprob` below does: a Worker older
        // than this field sends nothing, and undefined leaking into the type would be a third
        // state nobody handles. Absent and null are the same statement — nobody worked out who
        // was speaking — and the panel shows nothing at all for it.
        speakers: wire.transcript.speakers ?? null,
        createdAt: wire.transcript.createdAt,
      },
      // Already ordered by `segment_index` on the server. Re-sorted here anyway because the
      // citation convention ("Lecture 4 · 12:31") depends on the order being the recording's,
      // and a jumbled transcript is a wrong answer that looks like a right one.
      segments: wire.segments
        .map((segment) => ({
          id: segment.id,
          transcriptId: wire.transcript!.id,
          index: segment.index,
          startMs: segment.startMs,
          endMs: segment.endMs,
          speaker: segment.speaker,
          text: segment.text,
          // `?? null` guards the deploy window: a Worker older than this field sends nothing,
          // and undefined leaking into the type would be a third state nobody handles. Absent
          // and null are the same statement — the transcriber's confidence is not known.
          avgLogprob: segment.avgLogprob ?? null,
        }))
        .sort((a, b) => a.index - b.index),
    }
  },

  // ── phone lecture upload (`/record`) — LIVE ───────────────────────────────
  //
  // The two-call intake contract, driven from a phone this time instead of from the browser
  // recorder: POST /api/audio/uploads registers the job AND creates the lecture row, then the
  // bytes go up at the URL it hands back. Both calls carry the signed-in JWT — the capture token
  // is the PC worker's credential and this screen never touches it.
  //
  // WHY XMLHttpRequest AND NOT fetch. `fetch` has no upload-progress event in any shipping
  // browser (ReadableStream request bodies are Chrome-only and need HTTP/2 plus duplex: 'half').
  // A 100 MB lecture over phone data is minutes of silence otherwise, and a bar animated on a
  // timer would keep filling after the connection died — which is exactly the lie this pipeline
  // exists to avoid. XHR reports the bytes the browser actually handed to the socket, so the bar
  // stops when the upload stops. It is also what gives `abort()` a real cancel.

  uploadLecture: async ({
    file,
    courseCode,
    date,
    durationSec,
    onProgress,
    signal,
    resume,
    onRegistered,
  }: LectureUpload): Promise<CapturedLecture> => {
    const OFFLINE =
      'The recording did not go up — the network or the API stopped answering. The file is still ' +
      'picked here: try again without leaving this page.'

    // Whatever the phone recorded is what the desktop worker decodes. The Worker derives the
    // stored extension from the content type OR the filename, so both go, and a picker that
    // reports no type at all (Android sometimes does for .3ga) still lands the right extension.
    const contentType = file.type || 'application/octet-stream'

    const job =
      resume ??
      (await (async (): Promise<RegisteredUpload> => {
        const registered = await call<{
          jobId: string
          uploadUrl: string
          maxBytes: number
          lectureId: string | null
        }>('/api/audio/uploads', {
          method: 'POST',
          body: JSON.stringify({
            kind: 'lecture',
            origin: 'phone',
            contentType,
            filename: file.name,
            // The literal filename, not a prettied version of it. The owner recognises
            // 'Voice 013.m4a'; a lecture titled 'Voice 013' is this layer inventing a name.
            title: file.name,
            courseCode,
            recordedAt: date,
            // Absent rather than zero when the browser could not read it: the Worker's field is
            // nullable precisely so an unknown duration stays unknown.
            durationSec: durationSec ?? null,
          }),
        }).catch((error: unknown) => {
          throw error instanceof TypeError ? new Error(OFFLINE) : error
        })
        // The ceiling is checked BEFORE the bytes go out, so a too-long recording costs a
        // sentence instead of four minutes of uploading followed by a 413. The Worker enforces it
        // too — this is the copy that can still name the ways out while the file is in the picker.
        if (file.size > registered.maxBytes) throw tooLarge(file.size, registered.maxBytes)
        return {
          jobId: registered.jobId,
          uploadUrl: registered.uploadUrl,
          lectureId: registered.lectureId,
        }
      })())
    onRegistered?.(job)

    const token = await getAccessToken()
    if (!token) throw new NotSignedInError(job.uploadUrl)

    await new Promise<void>((resolve, reject) => {
      const xhr = new XMLHttpRequest()
      xhr.open('PUT', job.uploadUrl, true)
      xhr.setRequestHeader('authorization', `Bearer ${token}`)
      xhr.setRequestHeader('content-type', contentType)
      // NO Content-Length header, deliberately: it is a forbidden header name, so the browser
      // drops it — and passing the File itself is what makes the browser set it, which is the
      // only way the Worker's 411 is not hit.
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) onProgress?.(event.loaded, event.total)
      }
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve()
          return
        }
        let detail = ''
        try {
          const body = JSON.parse(xhr.responseText) as { message?: string }
          detail = typeof body.message === 'string' ? body.message : ''
        } catch {
          /* a non-JSON error body is not worth a second failure */
        }
        reject(new Error(detail || `Uploading the recording failed (${xhr.status}).`))
      }
      xhr.onerror = () => reject(new Error(OFFLINE))
      xhr.ontimeout = () => reject(new Error(OFFLINE))
      xhr.onabort = () =>
        reject(new Error('The upload was cancelled. The file is still picked here.'))
      if (signal) {
        if (signal.aborted) {
          xhr.abort()
          return
        }
        signal.addEventListener('abort', () => xhr.abort(), { once: true })
      }
      xhr.send(file)
    })

    // The lecture is READ BACK rather than assembled from what we sent. Its status moved to
    // `audio_uploaded` on the server as a consequence of the PUT, and a locally-invented row
    // would be a second account of the same recording that can disagree with the first.
    if (!job.lectureId) {
      throw new Error(
        'The audio is uploaded, but the job came back with no lecture attached to it, so there ' +
          'is nothing to open. The recording is safe in the queue — the desktop worker will still ' +
          'transcribe it.',
      )
    }
    const { lecture } = await call<{ lecture: WireCapturedLecture }>(
      `/api/lectures/${job.lectureId}`,
    )
    return toCapturedLecture(lecture)
  },

  // ── the durable queue's three calls (REC-1b) ──────────────────────────────
  //
  // WHY THESE DO NOT GO THROUGH `call()`. That helper collapses every failure into the Worker's
  // sentence, which is exactly right for a screen with a person reading it. The queue is a state
  // machine driven by the DIFFERENCE between failures: `409 upload_in_progress` means wait,
  // `409 upload_superseded` means reconcile, `409 job_failed` means this job will never accept
  // bytes again, and `503 storage_write_failed` means send the same file again with no new
  // registration. Matching on prose would be one copy edit away from a lecture uploaded forever.

  registerUpload: async (input: UploadRegistrationInput): Promise<UploadRegistration> => {
    const res = await uploadFetch('/api/audio/uploads', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({
        kind: 'lecture',
        origin: 'phone',
        clientUploadId: input.clientUploadId,
        contentType: input.contentType,
        filename: input.filename,
        title: input.title,
        courseCode: input.courseCode,
        recordedAt: input.date,
        durationSec: input.durationSec,
      }),
    })

    const body = await readJson(res)
    if (!res.ok) throw uploadError(res, body)

    const payload = body as {
      jobId: string
      uploadUrl: string
      lectureId: string | null
      maxBytes: number
    }
    return {
      jobId: payload.jobId,
      uploadUrl: payload.uploadUrl,
      lectureId: payload.lectureId ?? null,
      maxBytes: payload.maxBytes,
      // 201 created the job; 200 found the one an earlier call already made. Same body either way
      // — this is the only place the distinction survives, and it is for the log, not for logic.
      created: res.status === 201,
    }
  },

  putUploadBlob: async ({
    uploadUrl,
    blob,
    contentType,
    onProgress,
    signal,
  }: UploadBlobInput): Promise<UploadBlobResult> => {
    const token = await getAccessToken()
    if (!token) throw new UploadApiError(401, 'not_signed_in', SIGNED_OUT)

    // XMLHttpRequest, not fetch, for the reason `uploadLecture` above states at length: fetch has
    // no upload-progress event in any shipping browser, and a bar on a timer keeps filling after
    // the connection dies.
    return await new Promise<UploadBlobResult>((resolve, reject) => {
      const xhr = new XMLHttpRequest()
      xhr.open('PUT', uploadUrl, true)
      xhr.setRequestHeader('authorization', `Bearer ${token}`)
      xhr.setRequestHeader('content-type', contentType)
      // No Content-Length header: it is a forbidden header name, and passing the Blob itself is
      // what makes the browser set it — which is the only way the Worker's 411 is not hit.
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable) onProgress?.(event.loaded, event.total)
      }
      xhr.onload = () => {
        let body: unknown = null
        try {
          body = JSON.parse(xhr.responseText)
        } catch {
          /* a non-JSON body is handled below, by its status */
        }
        const shape = (body ?? {}) as {
          error?: string
          message?: string
          received?: boolean
          written?: boolean
          job?: { status?: string }
        }

        if (xhr.status >= 200 && xhr.status < 300) {
          const status = asServerStatus(shape.job?.status)
          if (!status) {
            // The Worker always sends the job back on a 2xx. Without it we do not know what the
            // job is, and "probably queued" is the guess that deletes a recording.
            reject(
              new UploadApiError(
                xhr.status,
                'unreadable_response',
                'The upload was accepted but the reply did not say what state the job is in. ' +
                  'Nothing is assumed; the queue will ask.',
              ),
            )
            return
          }
          resolve({
            // Both come off the wire. `received: false` is not a thing this Worker sends on a
            // 2xx, and defaulting it to true here would be this layer inventing a receipt.
            received: shape.received === true,
            written: shape.written === true,
            status,
            message: typeof shape.message === 'string' ? shape.message : null,
          })
          return
        }

        const retryAfter = Number(xhr.getResponseHeader('retry-after'))
        reject(
          new UploadApiError(
            xhr.status,
            shape.error ?? `http_${xhr.status}`,
            shape.message || `Uploading the recording failed (${xhr.status}).`,
            {
              serverStatus: asServerStatus(shape.job?.status),
              retryAfterSec: Number.isFinite(retryAfter) && retryAfter > 0 ? retryAfter : null,
            },
          ),
        )
      }
      xhr.onerror = () => reject(new UploadApiError(0, 'offline', UPLOAD_OFFLINE))
      xhr.ontimeout = () => reject(new UploadApiError(0, 'offline', UPLOAD_OFFLINE))
      xhr.onabort = () =>
        reject(new UploadApiError(0, 'aborted', 'That send was stopped. The recording is still here.'))
      if (signal) {
        if (signal.aborted) {
          xhr.abort()
          return
        }
        signal.addEventListener('abort', () => xhr.abort(), { once: true })
      }
      xhr.send(blob)
    })
  },

  getUploadState: async (jobId: string): Promise<UploadState> => {
    const res = await uploadFetch(`/api/audio/uploads/${jobId}`)
    const body = await readJson(res)
    if (!res.ok) throw uploadError(res, body)

    const payload = body as {
      job?: {
        status?: string
        clientUploadId?: string | null
        bytes?: number | null
        lastError?: string | null
      }
      lecture?: { id?: string } | null
    }
    const status = asServerStatus(payload.job?.status)
    if (!status) {
      throw new UploadApiError(
        res.status,
        'unreadable_response',
        'The read-back did not name a status this app knows, so nothing was concluded from it.',
      )
    }
    return {
      jobId,
      status,
      // `undefined` and `null` collapse to null on purpose — both mean "the server named no
      // client id for this job", and the queue treats that as "not my receipt" either way.
      clientUploadId: payload.job?.clientUploadId ?? null,
      bytes: payload.job?.bytes ?? null,
      lastError: payload.job?.lastError ?? null,
      lectureId: payload.lecture?.id ?? null,
    }
  },

  getCapturedLectures: async (limit = 20): Promise<CapturedLecture[]> => {
    const { lectures } = await call<{ lectures: WireCapturedLecture[] }>(
      `/api/lectures?limit=${limit}`,
    )
    return lectures.map(toCapturedLecture)
  },

  getCapturedLecture: async (id: string): Promise<CapturedLecture | null> => {
    try {
      const { lecture } = await call<{ lecture: WireCapturedLecture }>(`/api/lectures/${id}`)
      return toCapturedLecture(lecture)
    } catch (error) {
      // "No lecture with that id" is an ANSWER — the page draws a not-found state. Everything
      // else is still a failure and still throws.
      if (error instanceof Error && /No lecture with that id/.test(error.message)) return null
      throw error
    }
  },

  assignLectureCourse: async (id: string, courseCode: string | null): Promise<CapturedLecture> =>
    toCapturedLecture(
      await call<WireCapturedLecture>(`/api/lectures/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ courseCode }),
      }),
    ),

  // ── school · the three School screens (6a/6b/6c) ──────────────────────────
  // Split by what each thing actually depends on, because "School is V2" would be a half-truth:
  // courses, announcements and the syllabus file come down the Canvas connector (V2), while the
  // summary, flashcards and practice questions are read out of a transcript and therefore cannot
  // exist before the transcription system (V1). A card that names the wrong version sends the
  // owner off to wait for the wrong thing.
  getCourse: async (slug: string): Promise<Course | null> => {
    try {
      return toCourse(await call<WireCourse>(`/api/school/courses/${encodeURIComponent(slug)}`))
    } catch (error) {
      // A 404 here is a real answer — "no synced course by that name" — and the course page
      // draws it as a not-found state. Every other failure is still a failure.
      if (error instanceof Error && /No synced course/.test(error.message)) return null
      throw error
    }
  },

  getAnnouncements: async (filter?: AnnouncementFilter): Promise<Announcement[]> => {
    const params = new URLSearchParams()
    if (filter?.courseId) params.set('courseId', filter.courseId)
    if (filter?.unreadOnly) params.set('unreadOnly', 'true')
    if (filter?.limit) params.set('limit', String(filter.limit))
    const { announcements } = await call<{ announcements: WireAnnouncement[] }>(
      `/api/school/announcements${params.size ? `?${params}` : ''}`,
    )
    return announcements.map((row) => ({
      id: row.id,
      courseId: row.courseId ?? '',
      courseCode: row.courseCode ?? '',
      author: row.author,
      title: row.title,
      body: row.body,
      // Canvas always stamps an announcement; the wire allows null because the column does, and
      // a row without one is dropped rather than dated with the moment it was read.
      postedAt: row.postedAt ?? '',
      read: row.read,
    })).filter((row) => row.postedAt !== '')
  },

  markAnnouncementsRead: async (ids: string[]): Promise<string[]> => {
    if (ids.length === 0) return []
    const res = await call<{ marked: number; ids: string[] }>('/api/school/announcements/read', {
      method: 'POST',
      body: JSON.stringify({ ids }),
    })
    return res.ids
  },
  // ── the syllabus panel — LIVE ─────────────────────────────────────────────
  //
  // Every number on that panel came out of a blob of instructor-typed HTML that a model read
  // (apps/worker/src/routes/syllabus.ts). NULL IS THE COMMON ANSWER and not a failure: most
  // Berkeley instructors hand out a PDF, which the Canvas API does not expose, so there is
  // nothing to read and the panel says exactly that.
  //
  // `hasProvenance` is applied at the network boundary for the same reason the study set applies
  // it: a row written by an older schema can arrive shaped like an extract and carrying nothing,
  // and an unlabelled grading breakdown is indistinguishable from one the professor published.
  getSyllabus: async (courseId: string): Promise<SyllabusExtract | null> => {
    const wire = await call<SyllabusExtract | null>(
      `/api/school/syllabus/${encodeURIComponent(courseId)}`,
    )
    return hasProvenance(wire) ? wire : null
  },

  /**
   * THE SYLLABUS FILE, which is a different question from the one above.
   *
   * No `hasProvenance` guard here, and its absence is the point: nothing in this response was
   * written by a model. Canvas listed a PDF, `unpdf` read its text layer in the Worker, and a
   * transcription has no model to name. Guarding it would imply there was one.
   */
  getSyllabusDocument: async (courseId: string): Promise<SyllabusDocument | null> =>
    call<SyllabusDocument | null>(
      `/api/school/syllabus/${encodeURIComponent(courseId)}/document`,
    ),

  generateSyllabus: async (courseId: string): Promise<SyllabusExtract> => {
    // The Worker's own refusals travel up as their sentences — "no syllabus posted in bCourses",
    // "states no grading weights", "no ANTHROPIC_API_KEY" — because each names a different thing
    // the owner would do next, and collapsing them into "could not extract" throws that away.
    const wire = await call<SyllabusExtract>(
      `/api/school/syllabus/${encodeURIComponent(courseId)}`,
      { method: 'POST' },
    )
    if (!hasProvenance(wire)) {
      throw new Error(
        'The Worker returned a syllabus extract with no provenance, so it cannot be shown: ' +
          'nothing here may render a grading breakdown that cannot say which model read it.',
      )
    }
    return wire
  },
  // ── the lecture study set — LIVE since V4 ─────────────────────────────────
  //
  // ONE ENDPOINT BEHIND THREE READS. The summary, the flashcards and the practice questions are
  // made by one model call from one transcript and share one provenance, so they are stored in one
  // row and read in one request (apps/worker/src/routes/study.ts). Three endpoints would be three
  // chances for the panels on one screen to disagree about which model wrote them.
  //
  // NULL AND EMPTY ARE REAL STATES, not failures: nothing has been generated for this lecture yet.
  // The lecture screen says that in words rather than rendering three empty panels.
  getLectureSummary: async (lectureId: string): Promise<LectureSummary | null> =>
    (await readStudySet(lectureId)).summary,

  getFlashcards: async (lectureId: string): Promise<Flashcard[]> =>
    (await readStudySet(lectureId)).flashcards,

  getPracticeQuestions: async (lectureId: string): Promise<PracticeQuestion[]> =>
    (await readStudySet(lectureId)).practiceQuestions,

  /**
   * The write side of the same one endpoint. Nothing generates a study set on a schedule — the
   * transcript landing does not trigger it — so until this is pressed, "no summary yet" on the
   * lecture screen is the literal truth rather than a wait.
   */
  generateStudySet: async (lectureId: string): Promise<StudySet> => {
    const wire = await call<{
      summary: LectureSummary | null
      flashcards: Flashcard[]
      practiceQuestions: PracticeQuestion[]
      note: string | null
    }>(`/api/lectures/${encodeURIComponent(lectureId)}/study`, { method: 'POST' })
    return {
      summary: hasProvenance(wire.summary) ? wire.summary : null,
      flashcards: onlyProvenanced(wire.flashcards),
      practiceQuestions: onlyProvenanced(wire.practiceQuestions),
      // The degradation sentence, carried all the way to the screen. A tier that could not be
      // served is never silent (apps/worker/src/lib/anthropic.ts → chooseModel).
      note: wire.note ?? null,
    }
  },

  // ── tasks — LIVE ──────────────────────────────────────────────────────────
  //
  // The whole domain is real: apps/worker/src/routes/tasks.ts. The Tasks board, the Today
  // screen's reminders card and the ⌘K palette's non-link captures all end up here.
  //
  // TWO THINGS THE WIRE SHAPE CARRIES THAT `Task` DOES NOT — `source` and `externalId`, the
  // idempotency key and the recurrence provenance chain. They are dropped by `toTask` below
  // rather than widened into the shared type: the screens have nothing to say about them, and a
  // field in the contract that no implementation of the contract renders is a field that quietly
  // stops being maintained. An LLM reading /api/tasks still sees both.
  getTasks: async (filter?: TaskFilter) => {
    const query = new URLSearchParams()
    if (filter?.done !== undefined) query.set('done', String(filter.done))
    if (filter?.list) query.set('list', filter.list)
    if (filter?.dueBefore) query.set('dueBefore', filter.dueBefore)
    const suffix = query.toString() ? `?${query.toString()}` : ''
    const { tasks } = await call<{ tasks: WireTask[] }>(`/api/tasks${suffix}`)
    return tasks.map(toTask)
  },

  createTask: async (input: TaskInput) => {
    const created = await call<WireTask>('/api/tasks', {
      method: 'POST',
      body: JSON.stringify({
        title: input.title,
        due: input.due ?? null,
        list: input.list,
        reminderAt: input.reminderAt ?? null,
        estimateMin: input.estimateMin ?? null,
        rrule: input.rrule ?? null,
      }),
    })
    return toTask(created)
  },

  // Completing is a POST to its own endpoint rather than a PATCH of a flag, because completing a
  // REPEATING task also creates the next occurrence — one act, one request. The response carries
  // that new occurrence as `next`; the contract's `toggleTask` returns the task that was ticked,
  // and the hook's `invalidateQueries(['tasks'])` is what brings the new row onto the board.
  toggleTask: async (id: string, done: boolean) => {
    const { task } = await call<{ task: WireTask; next: WireTask | null }>(
      `/api/tasks/${id}/${done ? 'complete' : 'uncomplete'}`,
      { method: 'POST' },
    )
    return toTask(task)
  },

  // ABSENT AND NULL ARE DIFFERENT WORDS on the wire, so the body is built key by key rather
  // than spread: `{ due: null }` clears the date and `{}` changes nothing, which is exactly what
  // the Worker's PATCH promises. A `?? null` here would turn every unmentioned field into an
  // erasure the caller never asked for.
  updateTask: async (id: string, patch: TaskPatch) => {
    const body: Record<string, unknown> = {}
    if (patch.title !== undefined) body.title = patch.title
    if (patch.due !== undefined) body.due = patch.due
    if (patch.list !== undefined) body.list = patch.list
    if (patch.reminderAt !== undefined) body.reminderAt = patch.reminderAt
    if (patch.estimateMin !== undefined) body.estimateMin = patch.estimateMin
    if (patch.rrule !== undefined) body.rrule = patch.rrule
    const saved = await call<WireTask>(`/api/tasks/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    })
    return toTask(saved)
  },

  // DELETING IS NOT COMPLETING. A completed task stays on the board's done log; this is for the
  // rows that should never have been written down, and it does not come back.
  deleteTask: async (id: string) =>
    toTask(await call<WireTask>(`/api/tasks/${id}`, { method: 'DELETE' })),

  getReminders: async () => {
    const { tasks } = await call<{ tasks: WireTask[] }>('/api/tasks/reminders')
    return tasks.map(toTask)
  },

  // ── calendar — live since this branch ─────────────────────────────────────
  //
  // NOTHING IS MAPPED HERE, and that is deliberate. The Worker already answers in exactly the
  // shape the screen reads — including `date`, `startMinutes` and `endMinutes`, derived server
  // side in the deployment's timezone (apps/worker/src/lib/calendar.ts). A second derivation in
  // the browser would be a second opinion about which day a 11 PM block belongs to, and the two
  // would disagree on exactly two nights a year.
  //
  // THE READ-ONLY RULE NEEDS NO CODE ON THIS SIDE. Canvas deadlines arrive in their own array
  // with no ids that any write endpoint accepts, and `CalendarBlockInput` cannot spell
  // `source: 'canvas'`. There is nothing here to remember not to do.

  getCalendarRange: async (range: DateRange): Promise<CalendarRange> =>
    call<CalendarRange>(`/api/calendar?from=${range.from}&to=${range.to}`),

  createCalendarBlock: async (input: CalendarBlockInput): Promise<CalendarBlock> =>
    call<CalendarBlock>('/api/calendar/blocks', {
      method: 'POST',
      body: JSON.stringify({
        title: input.title,
        source: input.source,
        startsAt: input.startsAt,
        endsAt: input.endsAt,
        location: input.location ?? null,
        taskId: input.taskId ?? null,
        state: input.state ?? 'accepted',
        externalId: input.externalId ?? null,
        // STATED, not omitted: a new block has no reminders unless the picker added some, and
        // saying `[]` out loud is what makes a create and an update mean the same thing about a
        // field the API replaces rather than merges.
        reminders: input.reminders ?? [],
      }),
    }),

  updateCalendarBlock: async (id: string, patch: CalendarBlockPatch): Promise<CalendarBlock> =>
    // The patch is sent as given: an absent key means "no change" and an explicit null CLEARS
    // the field, the same contract the journal's PUT and tasks' PATCH keep. Spreading defaults
    // in here would turn every move into a full overwrite.
    call<CalendarBlock>(`/api/calendar/blocks/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),

  deleteCalendarBlock: async (id: string): Promise<CalendarBlock> =>
    call<CalendarBlock>(`/api/calendar/blocks/${id}`, { method: 'DELETE' }),

  // ── learning — live since V3 ──────────────────────────────────────────────
  //
  // PLANS IN, PROGRESS OUT. A curriculum arrives through POST /api/learning/tracks — from an
  // authoring agent, not from this app, which is why there is no ingestion method on the data
  // contract — and everything below reads it back.
  //
  // EVERY NUMBER HERE IS THE WORKER'S. Percent, the pace phrase, the current unit, the streak and
  // the week strip are all derived server-side (apps/worker/src/lib/learning.ts), for the reason
  // types.ts states above the Learning block: an LLM asking the Worker "am I behind on algebra"
  // has to get the same sentence the screen shows. Nothing is recomputed on this side.
  //
  // An empty list is now a REAL answer rather than a named absence: a curriculum nobody has
  // imported is genuinely not there, and the screens say so in their own words.
  getLearningTracks: async (): Promise<LearningTrack[]> =>
    (await call<{ tracks: LearningTrackSummary[] }>('/api/learning/tracks')).tracks.map(
      (summary) => summary.track,
    ),

  getLearningItems: async (date: ISODate) =>
    (await call<{ date: ISODate; items: LearningItem[] }>(`/api/learning/items?date=${date}`)).items,

  // Checking the box IS logging a session, and unchecking is deleting it — there is no `done`
  // column anywhere to fall out of step with the log, so the undo is exact.
  //
  // MINUTES AND NOTE RIDE ALONG when the workspace collected them. The Worker's write is
  // idempotent on (owner, item), so the same call made twice — once by the checkbox, once by the
  // "log it" form a moment later — is one session with a duration on it, not two.
  toggleLearningItem: (id: string, done: boolean, session?: LearningSessionInput) =>
    call<LearningItem>(`/api/learning/items/${encodeURIComponent(id)}/session`, {
      method: done ? 'POST' : 'DELETE',
      // The Worker's session body is optional; '{}' rather than nothing keeps the JSON
      // content-type this helper always sends honest. An unchecking carries no body at all —
      // there is nothing to attach a note to when the session is being deleted.
      ...(done ? { body: JSON.stringify(session ?? {}) } : {}),
    }),

  getLearningStreak: () => call<LearningStreak>('/api/learning/streak'),

  // ── learning · the two Learning screens (9a/9b) ───────────────────────────
  getLearningTrackSummaries: async () =>
    (await call<{ tracks: LearningTrackSummary[] }>('/api/learning/tracks')).tracks,

  // `trackId` is the uuid the summaries carry; the Worker also accepts the slug the plan was
  // imported under, which is what makes a hand-typed /learning/algebra-to-calculus URL work.
  // A track that does not exist answers null, not a 404 — the same shape the journal uses for a
  // day with nothing written.
  getLearningTrackDetail: (trackId: string) =>
    call<LearningTrackDetail | null>(`/api/learning/tracks/${encodeURIComponent(trackId)}`),

  // The library, DERIVED BY THE WORKER from the plans (the standing `resources` plus every link
  // the items name, deduplicated by url). Derived there and not here for the reason the pace
  // phrase is: a link count computed in the browser is a second answer to a question the API
  // already answers, and an agent asking "what am I reading for algebra" must get the same list.
  getLearningResources: async () =>
    (await call<{ tracks: LearningTrackLibrary[] }>('/api/learning/resources')).tracks,

  // The one read in this file whose body is not JSON. `/api/learning/export` answers
  // `text/markdown`, because the page's whole job is to be copied into a chat somewhere else.
  exportLearning: () => callText('/api/learning/export'),

  // ── interests — LIVE since V5 ─────────────────────────────────────────────
  //
  // The saves corpus, read back out of the Worker. Every number here is COUNTED IN POSTGRES
  // rather than in the browser (`/api/interests/overview` asks with `Prefer: count=exact`),
  // because the header band's promise is that it measured the library — a size computed from
  // whatever page happened to be fetched is a constant wearing a computation's clothes.
  //
  // WHAT IS STILL ABSENT, AND HONESTLY SO: `topic` is null on every save until the clustering
  // job exists, so `getInterestTopics` returns an empty list — and an empty cluster row is a
  // TRUE statement ("nothing has been grouped yet"), unlike an empty library, which would be a
  // false one. The AI two-liner is null until a model has actually read a save (V4); the Worker
  // drops any summary that arrives without provenance rather than rendering it.
  getInterestSaves: async (filter?: SaveFilter): Promise<InterestSave[]> => {
    const query = new URLSearchParams()
    if (filter?.topic) query.set('topic', filter.topic)
    if (filter?.unread !== undefined) query.set('unread', String(filter.unread))
    if (filter?.limit) query.set('limit', String(filter.limit))
    const suffix = query.toString() ? `?${query.toString()}` : ''
    return (await call<{ saves: InterestSave[] }>(`/api/interests/saves${suffix}`)).saves
  },

  getInterestTopics: async (): Promise<InterestTopic[]> =>
    (await call<{ topics: InterestTopic[] }>('/api/interests/topics')).topics,

  getInterestsOverview: () => call<InterestsOverview>('/api/interests/overview'),

  // A save that does not exist answers 404, and the contract asks for null — "no save has that
  // id" is a different statement from "the save is empty", and the pane says the first one.
  getInterestSave: async (id: string): Promise<InterestSaveDetail | null> => {
    try {
      return await call<InterestSaveDetail>(`/api/interests/saves/${encodeURIComponent(id)}`)
    } catch (error) {
      if (error instanceof Error && /No save with that id/.test(error.message)) return null
      throw error
    }
  },

  // ── connections — the trust surface reports on systems that do not exist yet ─
  //
  // This screen is built early on purpose (handoff §Suggested build order 4:
  // "every other screen's honest-degradation state depends on connector and
  // worker status being real"). Building it early means it reports on four
  // backends, none of which is finished — so each read names the version that
  // owns it, and the screen renders that sentence instead of an empty card.
  // An empty connector list here would claim "you have connected nothing",
  // which is a different and false statement.

  // LIVE since V2. Canvas as it actually is, then the connectors that are designed and not
  // built — the Worker returns the planned ones too, because "we know, and not yet" is a fact
  // about this system and the machine-readability rule has no boring-data exemption.
  getConnectors: async (): Promise<Connector[]> =>
    (await call<{ connectors: Connector[] }>('/api/connections')).connectors,

  // LIVE. Derived entirely from `audio_jobs` — there is no heartbeat from the PC, so `state` is
  // only ever 'online' when a job is provably claimed right now, never guessed from recency
  // (apps/worker/src/lib/worker-status.ts carries the full derivation and its honesty rules).
  getDesktopWorker: (): Promise<DesktopWorkerStatus> => call<DesktopWorkerStatus>('/api/worker-status'),

  // LIVE since V1. `capturesThisMonth` is always null here: the Worker's capture-tokens table has
  // no per-token usage counter yet (routes/capture-tokens.ts), and the type's own contract is to
  // render nothing rather than a guessed 0.
  getCaptureTokens: async (): Promise<CaptureToken[]> => {
    const { tokens } = await call<{
      tokens: Array<{
        id: string
        label: string
        device: CaptureToken['device']
        lastUsedAt: string | null
        revokedAt: string | null
        createdAt: string
      }>
    }>('/api/capture-tokens')
    return tokens.map((t) => ({ ...t, capturesThisMonth: null }))
  },

  // LIVE (the Worker route predates this caller). The plaintext comes back once; the only caller
  // passes it straight to the Android uploader (lib/native-upload.ts) and keeps no copy.
  createCaptureToken: async (input: CaptureTokenInput): Promise<CreatedCaptureToken> => {
    const created = await call<CreatedCaptureToken>('/api/capture-tokens', {
      method: 'POST',
      body: JSON.stringify({ label: input.label, ...(input.device ? { device: input.device } : {}) }),
    })
    if (typeof created?.token !== 'string' || created.token.length === 0) {
      throw new Error('The server created a token but did not return it. Revoke it on Connections and try again.')
    }
    return {
      id: created.id,
      label: created.label,
      device: created.device,
      createdAt: created.createdAt,
      token: created.token,
    }
  },

  // LIVE. Journal entries, tasks and saves are counted as indexed unconditionally — their
  // embedding runs fire-and-forget with no stored per-row outcome, by design. Transcripts are the
  // one table that tracks success and failure explicitly, so `pending` counts only those
  // (apps/worker/src/routes/data-status.ts documents the full accounting).
  getDataStatus: (): Promise<DataStatus> => call<DataStatus>('/api/data-status'),

  // LIVE since V2. Runs the same adapter the six-hourly cron runs: archive raw, then upsert
  // courses/assignments/announcements. `id` names the connector so the Worker route can grow one
  // sync endpoint per connector without this method changing shape; today only Canvas is real.
  syncConnector: (id: string) => call<SyncResult>(`/api/connections/${id}/sync`, { method: 'POST' }),

  // ── search (10a/10b) — LIVE since V4 ──────────────────────────────────────
  //
  // ONE ENDPOINT, and it fuses both halves server-side: Postgres full-text over the `search_tsv`
  // columns (stemmed, so "midterm" finds "midterms") and Cloudflare Vectorize nearest-neighbours
  // over the embeddings written on every insert since V1, combined with Reciprocal Rank Fusion
  // (CLAUDE.md → Architecture). Nothing is ranked or merged in the browser: the fusion has to
  // happen where both halves are, and doing half of it here is how two rankings start disagreeing.
  //
  // `relatedBasis` COMES BACK FROM THE SERVER AND IS NOT OVERWRITTEN. In remote mode it is
  // 'vector' when Vectorize actually answered and 'unavailable' when it could not — a distinction
  // the screen prints, because an empty related block means two different things under those two
  // words and only one of them is "there is nothing near this".
  search: (query: string) =>
    call<SearchResults>(`/api/search?q=${encodeURIComponent(query)}`),
  // ── chat (11a/11b) — LIVE since V4 · the ⌘K capture palette (11c) is V5 ───
  //
  // ONE ENGINE, four reads and one write (apps/worker/src/routes/chat.ts). Asking is a single
  // call whether or not a thread exists yet, because the Worker has a single path for both: a
  // thread holding a question with no answer under it is a conversation that half happened, and
  // splitting this into create-then-ask would make that state reachable.
  //
  // The wire shapes are a SUPERSET of what the screens speak — the Worker also answers each
  // thread's `persona` and each assistant message's `model` and `tier`. Those are provenance, and
  // they are one documented GET away for anything that wants them; the mappers below narrow to the
  // vocabulary the components render, exactly as the School mappers do.
  getChatThreads: async (): Promise<ChatThread[]> =>
    (await call<{ threads: WireChatThread[] }>('/api/chat/threads')).threads.map(toChatThread),

  getChatThread: async (threadId: string): Promise<ChatThreadDetail | null> => {
    try {
      const wire = await call<{ thread: WireChatThread; messages: WireChatMessage[] }>(
        `/api/chat/threads/${encodeURIComponent(threadId)}`,
      )
      return { thread: toChatThread(wire.thread), messages: wire.messages.map(toChatMessage) }
    } catch (error) {
      // "No thread with that id" is a real answer — the screen draws a not-found state for it —
      // and every other failure is still a failure.
      if (error instanceof Error && /No thread with id/.test(error.message)) return null
      throw error
    }
  },

  getChatScopes: async (): Promise<ChatScope[]> =>
    (await call<{ scopes: WireChatScope[] }>('/api/chat/scopes')).scopes.map((scope) => ({
      ...scope,
      id: scope.id as ChatScopeId,
    })),

  getChatStarters: async (): Promise<ChatStarter[]> =>
    (await call<{ starters: WireChatStarter[] }>('/api/chat/starters')).starters.map((starter) => ({
      ...starter,
      scopeId: starter.scopeId as ChatScopeId,
    })),

  /**
   * ASK. The one write on this screen.
   *
   * It RESOLVES even when the answer cites nothing — that message comes back with an empty
   * `sources` array and the conversation renders it behind the red "unverified" strip. Turning it
   * into a thrown error here would hide the one failure the design deliberately shows.
   */
  sendChatMessage: async (input: ChatSendInput): Promise<ChatSendResult> => {
    const wire = await call<{
      thread: WireChatThread
      messages: WireChatMessage[]
      note: string | null
    }>('/api/chat/messages', {
      method: 'POST',
      body: JSON.stringify({
        threadId: input.threadId,
        text: input.text,
        scopeId: input.scopeId,
        tier: input.tier,
        ...(input.attachmentIds?.length ? { attachmentIds: input.attachmentIds } : {}),
      }),
    })
    return {
      thread: toChatThread(wire.thread),
      messages: wire.messages.map(toChatMessage),
      note: wire.note,
    }
  },

  // GONE, NOT ARCHIVED. The messages go with the thread by foreign key inside Postgres, so this
  // is one request and there is no half-deleted state for the list to render.
  deleteChatThread: async (threadId: string): Promise<ChatThread> => {
    const wire = await call<{ thread: WireChatThread; deletedMessages: number }>(
      `/api/chat/threads/${encodeURIComponent(threadId)}`,
      { method: 'DELETE' },
    )
    return toChatThread(wire.thread)
  },

  /**
   * SEND A PICTURE UP. Raw bytes, not multipart — the same shape the audio upload uses.
   *
   * NO Content-Length HEADER, deliberately: it is a forbidden header name, so a browser silently
   * drops it, and passing a Blob (not a stream) is what makes the browser set it itself. That is
   * the only way the Worker's 411 branch can ever be satisfied — the audio upload learned this
   * the hard way and the comment travels with the pattern.
   */
  uploadChatAttachment: async (file: Blob): Promise<ChatAttachment> => {
    const base = apiBase()
    if (!base) throw new NotConfiguredError('/api/chat/attachments')
    const token = await getAccessToken()
    if (!token) throw new NotSignedInError('/api/chat/attachments')

    const res = await fetch(`${base}/api/chat/attachments`, {
      method: 'POST',
      headers: {
        authorization: `Bearer ${token}`,
        'content-type': file.type || 'image/png',
      },
      body: file,
    })
    if (!res.ok) {
      let detail = ''
      try {
        const body = (await res.json()) as { message?: string }
        detail = typeof body.message === 'string' ? body.message : ''
      } catch {
        /* a non-JSON error body is not worth a second failure */
      }
      throw new Error(detail || `Sending the picture failed (${res.status}).`)
    }
    return (await res.json()) as ChatAttachment
  },

  /**
   * An object URL for one already-sent picture.
   *
   * The bytes are behind the owner's token, so an `<img src>` pointing at the API would 401 —
   * a browser does not send an Authorization header on an image request. So the blob is fetched
   * here and handed over as an object URL. The caller revokes it when the element unmounts.
   */
  getChatAttachmentUrl: async (attachmentId: string): Promise<string> => {
    const path = `/api/chat/attachments/${encodeURIComponent(attachmentId)}`
    const base = apiBase()
    if (!base) throw new NotConfiguredError(path)
    const token = await getAccessToken()
    if (!token) throw new NotSignedInError(path)

    const res = await fetch(`${base}${path}`, { headers: { authorization: `Bearer ${token}` } })
    if (!res.ok) throw new Error(`That picture could not be loaded (${res.status}).`)
    return URL.createObjectURL(await res.blob())
  },

  /**
   * ACCEPT — the only path from a proposal to a real row.
   *
   * A second accept is REFUSED by the Worker with a 409 rather than creating a second block, and
   * that refusal arrives here as its own sentence. It is not smoothed over: a double-tap that
   * silently succeeded twice would put two identical appointments on the calendar.
   */
  acceptChatProposal: (proposalId: string): Promise<ChatProposalDecision> =>
    call<{ proposal: ChatProposal; created: ChatProposalResult }>(
      `/api/chat/proposals/${encodeURIComponent(proposalId)}/accept`,
      { method: 'POST' },
    ),

  rejectChatProposal: async (proposalId: string): Promise<ChatProposal> =>
    (
      await call<{ proposal: ChatProposal }>(
        `/api/chat/proposals/${encodeURIComponent(proposalId)}/reject`,
        { method: 'POST' },
      )
    ).proposal,

  // LIVE since V5. The Worker really opens the page (a 4-second budget, a real User-Agent) and
  // answers `title: null` when it could not read one — never a headline derived from the slug.
  getClipPreview: (url: string): Promise<ClipPreview | null> =>
    call<ClipPreview>(`/api/capture/preview?url=${encodeURIComponent(url)}`),

  /**
   * QUICK CAPTURE — LIVE, and now genuinely one door.
   *
   * A note, a task and an unset capture all land in the Inbox, which is the tasks list of that
   * name (the same rule the demo implementation states: LifeOS has exactly one Inbox). A reminder
   * is an ambient task — it is `reminderAt` that puts a row on the Today card, not a second kind
   * of row — so it is the same write with a time on it. A LINK BECOMES A SAVE in Interests: a
   * link is not a task, and filing one as a task would put it somewhere the owner did not ask
   * for and then report success.
   *
   * WHY EVERYTHING GOES THROUGH `/api/capture/quick` AND NOT THROUGH `/api/tasks`. Until V5 the
   * palette wrote tasks through the domain endpoint, because the palette holds a JWT and the
   * capture door did not exist. It exists now, it takes either credential, and it is where the
   * "which domain does this belong in" decision lives. Two implementations of that decision —
   * one here, one on the Worker for the share sheet — is exactly how the phone and the palette
   * end up filing the same link in two different places (CLAUDE.md → Architecture: ONE endpoint,
   * many mouths).
   *
   * `reminderAt` IS COMPUTED HERE, not left to the server, for the one reason the server cannot
   * fix: 09:00 means 09:00 on the OWNER'S clock, and the browser is the only party to this
   * request that knows what that is. The Worker falls back to its own configured timezone when a
   * caller (the share sheet, the PC hotkey) cannot say.
   */
  captureQuick: async (input: CaptureInput): Promise<CaptureResult> => {
    const now = new Date()
    const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`

    const result = await call<CaptureResult>('/api/capture/quick', {
      method: 'POST',
      body: JSON.stringify({
        text: input.text,
        type: input.type,
        due: input.due ?? null,
        // A chosen instant wins; otherwise the 9:00 default the comment above explains.
        reminderAt:
          input.type === 'reminder' ? (input.reminderAt ?? morningOf(input.due ?? today)) : null,
      }),
    })

    // Read off what came back, never off what was asked for: an unset capture that turned out to
    // be a link comes back as a `clip` filed under Interests, and the palette says so.
    return { id: result.id, type: result.type, destination: result.destination, href: result.href }
  },

  // ── notifications · Web Push — live since NOTIF-1a ────────────────────────
  //
  // NOTHING IS MAPPED HERE. The Worker answers in exactly the shapes types.ts declares, because
  // those shapes were transcribed from its own zod schemas. A second opinion in the browser about
  // what a notification row looks like is how the screen and `GET /api/notifications` start
  // disagreeing about the same row.
  //
  // A 503 ARRIVES AS THE WORKER'S SENTENCE, not as a status code — `call()` above unwraps
  // `{ error, message }`. That is the whole of the "push not configured" story on this side: the
  // deployment without VAPID keys says so in words, and the screen prints the words.

  getPushPublicKey: async (): Promise<string> =>
    (await call<{ publicKey: string }>('/api/push/public-key')).publicKey,

  getPushSubscriptions: async (): Promise<PushDevice[]> =>
    (await call<{ subscriptions: PushDevice[] }>('/api/push/subscriptions')).subscriptions,

  // 201 the first time this endpoint is seen, 200 afterwards, and the row comes back either way —
  // which is why this does not care which it got. Re-subscribing re-enables a row a 410 disabled.
  subscribeToPush: async (input: PushSubscribeInput): Promise<PushDevice> =>
    call<PushDevice>('/api/push/subscriptions', {
      method: 'POST',
      body: JSON.stringify({
        endpoint: input.endpoint,
        keys: input.keys,
        // Absent rather than null: the API's `label` is optional, and sending null would be the
        // browser asserting "this device has no name" over a name the server already stored.
        ...(input.label ? { label: input.label } : {}),
        ...(input.userAgent ? { userAgent: input.userAgent } : {}),
      }),
    }),

  unsubscribeFromPush: async (id: string): Promise<PushDevice> =>
    call<PushDevice>(`/api/push/subscriptions/${id}`, { method: 'DELETE' }),

  sendTestNotification: async (): Promise<PushTestResult> =>
    call<PushTestResult>('/api/notifications/test', { method: 'POST' }),

  getNotifications: async (filter?: NotificationFilter): Promise<NotificationRecord[]> => {
    const query = new URLSearchParams()
    if (filter?.since) query.set('since', filter.since)
    if (filter?.status) query.set('status', filter.status)
    if (filter?.limit !== undefined) query.set('limit', String(filter.limit))
    const search = query.toString()
    const suffix = search ? `?${search}` : ''
    return (await call<{ notifications: NotificationRecord[] }>(`/api/notifications${suffix}`))
      .notifications
  },

  getNotificationSettings: async (): Promise<NotificationSettings> =>
    call<NotificationSettings>('/api/notifications/settings'),

  // SENT AS GIVEN. The PUT is partial and merges `kindsEnabled` key by key on the server, so
  // filling in the fields this call is not changing would turn every switch into a full
  // overwrite of the other three.
  updateNotificationSettings: async (
    patch: NotificationSettingsPatch,
  ): Promise<NotificationSettings> =>
    call<NotificationSettings>('/api/notifications/settings', {
      method: 'PUT',
      body: JSON.stringify(patch),
    }),

  // ── mail · Google accounts for read-only Gmail (Day page, Lane 1a) ────────
  //
  // The same rule as notifications: no mapping, because the Worker answers in the shapes types.ts
  // transcribed from its schemas. A 503 (Google sign-in not set up) arrives as the Worker's own
  // sentence, which names the missing variables — the card prints it as it is.

  listMailAccounts: async (): Promise<MailAccount[]> =>
    (await call<{ accounts: MailAccount[] }>('/api/mail/accounts')).accounts,

  startGoogleMailConnect: async (): Promise<string> =>
    (await call<{ url: string }>('/api/mail/google/start', { method: 'POST' })).url,

  disconnectMailAccount: async (id: string): Promise<MailDisconnectResult> =>
    call<MailDisconnectResult>(`/api/mail/accounts/${encodeURIComponent(id)}`, { method: 'DELETE' }),

  // Lane 1b. Only the filters that were given go on the query string — an absent `since` is "no
  // lower bound", and sending `since=` would be a 400.
  listMailMessages: async (filter: MailMessageFilter = {}): Promise<MailMessagePage> => {
    const params = new URLSearchParams()
    if (filter.account) params.set('account', filter.account)
    if (filter.since) params.set('since', filter.since)
    if (filter.limit !== undefined) params.set('limit', String(filter.limit))
    if (filter.cursor) params.set('cursor', filter.cursor)
    const qs = params.toString()
    return call<MailMessagePage>(`/api/mail/messages${qs ? `?${qs}` : ''}`)
  },

  // ── mail · triage, grading and rules (Day page prototype) ────────────────
  //
  // No mapping: the Worker answers in the shapes types.ts transcribed. Optional fields go on the
  // body only when given — an absent `reason` is "no reason", and `reason: undefined` would vanish
  // from JSON anyway, but a `null` would be a different (and invalid) request.

  // `scenario` is demo-only and never leaves the browser. `days` rides as `?days=` — the designed
  // Day page asks for the last 7; the prototype asks for the Worker's default by sending nothing.
  //
  // THE ONE MAPPING: a Worker from before Lane 6a sends no `proposals` / `proposalsAt`. Absent is
  // read as "the Worker has not proposed for this message yet" (`[]`, `null`) — which is exactly
  // true — so the page says "Next steps not ready yet" instead of failing on `undefined`.
  getMailTriage: async (query?: MailTriageQuery): Promise<MailTriage> =>
    withProposalFields(
      await call<MailTriage>(query?.days !== undefined ? `/api/mail/triage?days=${query.days}` : '/api/mail/triage'),
    ),

  getMailAudit: async (weekStart: ISODate): Promise<MailAudit> =>
    call<MailAudit>(`/api/mail/audit?weekStart=${encodeURIComponent(weekStart)}`),

  setMailVerdict: async (
    id: string,
    verdict: MailVerdict,
    reason?: MailCorrectionReason,
  ): Promise<MailTriageItem> =>
    call<MailTriageItem>(`/api/mail/messages/${encodeURIComponent(id)}/verdict`, {
      method: 'POST',
      body: JSON.stringify(reason ? { verdict, reason } : { verdict }),
    }),

  setMailState: async (
    id: string,
    state: MailItemState,
    snoozedUntil?: string,
  ): Promise<MailTriageItem> =>
    call<MailTriageItem>(`/api/mail/messages/${encodeURIComponent(id)}/state`, {
      method: 'POST',
      body: JSON.stringify(snoozedUntil ? { state, snoozedUntil } : { state }),
    }),

  listMailRules: async (): Promise<MailRule[]> =>
    (await call<{ rules: MailRule[] }>('/api/mail/rules')).rules,

  // The two rule writes answer nothing the page reads (it re-reads the triage), so the body is
  // taken as text: an empty 204 must not fail a write that succeeded.
  createMailRule: async (input: MailRuleInput): Promise<void> => {
    await callText('/api/mail/rules', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ matchType: input.matchType, matchValue: input.matchValue, verdict: input.verdict }),
    })
  },

  deleteMailRule: async (id: string): Promise<void> => {
    await callText(`/api/mail/rules/${encodeURIComponent(id)}`, { method: 'DELETE' })
  },

  runMailTriage: async (limit?: number): Promise<MailTriageRunResult> =>
    call<MailTriageRunResult>('/api/mail/triage/run', {
      method: 'POST',
      body: JSON.stringify(limit !== undefined ? { limit } : {}),
    }),

  // ── mail · next steps (Day page, Lane 6) ─────────────────────────────────
  // Each answers `{ proposal, item }`. No body: the id in the path is the whole request.

  acceptMailProposal: async (id: string): Promise<MailProposalDecision> =>
    call<MailProposalDecision>(`/api/mail/proposals/${encodeURIComponent(id)}/accept`, { method: 'POST' }),

  skipMailProposal: async (id: string): Promise<MailProposalDecision> =>
    call<MailProposalDecision>(`/api/mail/proposals/${encodeURIComponent(id)}/skip`, { method: 'POST' }),

  undoMailProposal: async (id: string): Promise<MailProposalDecision> =>
    call<MailProposalDecision>(`/api/mail/proposals/${encodeURIComponent(id)}/undo`, { method: 'POST' }),
}

/** See `getMailTriage`: a pre-Lane-6a Worker's items get `proposals: []`, `proposalsAt: null`. */
export function withProposalFields(t: MailTriage): MailTriage {
  const fix = (list: MailTriageItem[] | undefined) =>
    list?.map((m) => ({ ...m, proposals: m.proposals ?? [], proposalsAt: m.proposalsAt ?? null }))
  const g = t.groups
  return {
    ...t,
    groups: {
      ...g,
      needsYouToday: fix(g.needsYouToday)!,
      important: fix(g.important)!,
      canWait: fix(g.canWait)!,
      noise: fix(g.noise)!,
      unsorted: fix(g.unsorted)!,
      ...(g.snoozed ? { snoozed: fix(g.snoozed) } : {}),
    },
  }
}
